import { useEffect, useState } from 'react';
import {
  FinanceApplicationStatus,
  FinanceProvider,
} from '@upstart/harmony/lib/constants';
import {
  PLACEHOLDER_AWAITING_OFFER_DETAILS,
  PLACEHOLDER_LONGER_THAN_EXPECTED,
  PLACEHOLDER_MINUTE_WARNING,
  PLACEHOLDER_WAITING_FOR_OFFERS,
} from '@upstart/desking-components';
import {
  FinanceApplication,
  FinanceResponse,
} from '@upstart/harmony/lib/legos/financingTypes';
import moment from 'moment/moment';

const ONE_MINUTE = 1000 * 60; // Time limit for displaying minute warning
const FIVE_MINUTES = 1000 * 60 * 5;

export default function useUpstartPlaceholderTile({
  financeApplications,
  financeResponses,
}: {
  financeApplications?: FinanceApplication[] | null;
  financeResponses?: FinanceResponse[] | null;
}): {
  showPlaceholderTile: boolean;
  placeholderRowOneText?: string;
  placeholderRowTwoText?: string;
} {
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholderText, setPlaceholderText] = useState<{
    rowOne?: string;
    rowTwo?: string;
  }>({});
  // Handle placeholder loading logic
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (!financeApplications || !financeResponses) {
      return undefined;
    }

    const showFiveMinutePlaceholder = (ms: number) => {
      setShowPlaceholder(true);
      setPlaceholderText({
        rowOne: PLACEHOLDER_AWAITING_OFFER_DETAILS,
        rowTwo: PLACEHOLDER_LONGER_THAN_EXPECTED,
      });
      clearTimeout(timer);
      timer = setTimeout(() => {
        setShowPlaceholder(false);
      }, ms);
    };

    const showMinutePlaceholder = (ms: number) => {
      setShowPlaceholder(true);
      setPlaceholderText({
        rowOne: PLACEHOLDER_WAITING_FOR_OFFERS,
        rowTwo: PLACEHOLDER_MINUTE_WARNING,
      });
      clearTimeout(timer);
      timer = setTimeout(() => {
        showFiveMinutePlaceholder(FIVE_MINUTES - ONE_MINUTE);
      }, ms);
    };

    const upstartFinanceApplication = financeApplications.find(
      fa =>
        fa.provider === FinanceProvider.UPSTART &&
        fa.status === FinanceApplicationStatus.ACTIVE,
    );

    if (
      upstartFinanceApplication &&
      !financeResponses.find(
        fr => fr.applicationId === upstartFinanceApplication.id,
      )
    ) {
      const now = moment();
      const createdAt = moment(upstartFinanceApplication.createdAt);
      const msSinceApplicationSent = now.diff(createdAt);

      if (msSinceApplicationSent < ONE_MINUTE) {
        showMinutePlaceholder(ONE_MINUTE - msSinceApplicationSent);
      } else if (msSinceApplicationSent < FIVE_MINUTES) {
        showFiveMinutePlaceholder(FIVE_MINUTES - msSinceApplicationSent);
      } else {
        setShowPlaceholder(false);
      }
    } else {
      // If no active upstart application found or if an upstart response is found, do not show placeholder
      setShowPlaceholder(false);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [financeApplications, financeResponses]);

  return {
    showPlaceholderTile: showPlaceholder,
    placeholderRowOneText: placeholderText.rowOne,
    placeholderRowTwoText: placeholderText.rowTwo,
  };
}
